import { ErrorHandler, Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { ReplaySubject, combineLatest } from 'rxjs'
import { FirebaseLemonaid, FirebaseLemonator } from '../service/firebase-lemonator.service'

export interface TilintarkastajaTiedot {
  uid: string
  tilintarkastajaAvain: string
}

@Injectable()
export class KirjautunutTilintarkastajaService {
  private nykyisetTilintarkastajaTiedot: TilintarkastajaTiedot | null = null
  private tilintarkastajaSubject = new ReplaySubject<TilintarkastajaTiedot | null>(1)

  /** Observable tilintarkastajan kirjautumistilasta */
  tilintarkastajaObservable = this.tilintarkastajaSubject.asObservable()

  constructor(
    private _firebaseLemonator: FirebaseLemonator,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _router: Router,
    private _errorHandler: ErrorHandler,
  ) {
    // Kuuntele Firebase auth-tilaa molemmista projekteista
    combineLatest([this._firebaseLemonator.authUserObservable, this._firebaseLemonaid.authUserObservable]).subscribe({
      next: async ([lemonatorUser, lemonaidUser]) => {
        // console.log('TILINTARKASTAJA users', lemonatorUser, lemonaidUser)

        try {
          if (!lemonatorUser || !lemonaidUser) {
            // console.log('Jompikumpi käyttäjistä on null', lemonatorUser, lemonaidUser)
            this.asetaTilintarkastajaNulliksi(!lemonatorUser && !lemonaidUser)
            return
          }

          if (this.nykyisetTilintarkastajaTiedot === null || this.nykyisetTilintarkastajaTiedot.uid !== lemonatorUser.uid) {
            const lemonatorTokenPromise = lemonatorUser.getIdTokenResult()
            const lemonaidTokenPromise = lemonaidUser.getIdTokenResult()

            const result = await Promise.all([lemonatorTokenPromise, lemonaidTokenPromise])

            if (result && result[0] && result[0].claims && result[0].claims.tilintarkastaja && result[1] && result[1].claims && result[1].claims.tilintarkastaja) {
              if (result[0].claims.tilintarkastaja === result[1].claims.tilintarkastaja) {
                const tiedot: TilintarkastajaTiedot = {
                  uid: lemonatorUser.uid,
                  tilintarkastajaAvain: result[0].claims.tilintarkastaja as string,
                }
                this.nykyisetTilintarkastajaTiedot = tiedot
                this.tilintarkastajaSubject.next(tiedot)
                // console.log('Tilintarkastaja kirjautunut:', lemonatorUser.uid, 'avain:', tiedot.tilintarkastajaAvain)
              } else {
                console.error('Palautuneet tilintarkastaja-claimit eivät täsmää')
                await this.kirjauduUlos()
              }
            } else {
              this.asetaTilintarkastajaNulliksi(true)
              // console.error('Käyttäjällä ei ole tilintarkastaja-claimia molemmissa projekteissa')
              // await this.kirjauduUlos()
            }
          }
        } catch (err) {
          console.error('Virhe auth-tilan käsittelyssä:', err)
          this.kirjauduUlos().catch(er => {
            this._errorHandler.handleError(er)
          })
          this._errorHandler.handleError(err)
        }
      },
      error: error => {
        console.error('Virhe auth-tilan kuuntelussa:', error)
        this.kirjauduUlos().catch(er => {
          this._errorHandler.handleError(er)
        })
        this._errorHandler.handleError(error)
      },
    })
  }

  /** Palauttaa true jos tilintarkastaja on kirjautunut */
  get onKirjautunut(): boolean {
    return this.nykyisetTilintarkastajaTiedot !== null
  }

  /** Palauttaa kirjautuneen tilintarkastajan tiedot tai null */
  get tilintarkastaja(): TilintarkastajaTiedot | null {
    return this.nykyisetTilintarkastajaTiedot
  }

  /**
   * Kirjaa tilintarkastajan sisään custom tokeneilla Lemonatoriin ja Lemonaidiin, ja ohjaa aloitussivulle
   * @param lemonatorToken Firebase custom token Lemonatoriin
   * @param lemonaidToken Firebase custom token Lemonaidiin
   * @returns Promise joka resolvoituu kun kirjautuminen on valmis
   */
  async kirjauduCustomTokenilla(lemonatorToken: string, lemonaidToken: string): Promise<void> {
    // Kirjaudu Firebase-autentikaatioon molempiin projekteihin
    await Promise.all([this._firebaseLemonator.authSignInWithCustomToken(lemonatorToken, false), this._firebaseLemonaid.authSignInWithCustomToken(lemonaidToken, false)])

    // Ohjaa aloitussivulle
    await this._router.navigate(['/tilintarkastajat/aloitus'])
  }

  /**
   * Kirjaa tilintarkastajan ulos
   */
  async kirjauduUlos(): Promise<void> {
    console.log('Kirjaudutaan ulos')
    this.asetaTilintarkastajaNulliksi(true)
    await this._firebaseLemonator.authSignOut()
    await this._firebaseLemonaid.authSignOut()
    await this._router.navigate(['/tilintarkastajat/kirjaudu'])
  }

  /** Force, jotta emitoidaan pakotettuna (päivittää observablen ja kuuntelijat saavat uuden tiedon) */
  private asetaTilintarkastajaNulliksi(force: boolean = false) {
    console.log('Asetetaan tilintarkastaja nulliksi', force, this.nykyisetTilintarkastajaTiedot)
    if (force || this.nykyisetTilintarkastajaTiedot !== null) {
      this.nykyisetTilintarkastajaTiedot = null
      this.tilintarkastajaSubject.next(null)
    }
  }
}
